import React, { useEffect, useMemo, useState } from 'react';

import { List } from 'antd';

import styled from 'styled-components';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { useContactPlaylists } from '@hooks/API/Contact/UseContactPlaylists';

import { Loading } from '@components/Gui/Loading';
import PublishedBookListItem from '@components/MediaDrawer/PublishedBookListItem';
import EmptyState from '@components/Gui/EmptyState';
import NwDrawer from '@components/Gui/NwDrawer';
import NwIcon from '@components/Gui/NwIcon';

import { coercePlaylistArray } from './modelDetailsBooks.utils';
import {
    buildModelPublicationsSummary,
    getPlaylistPublicationKeys,
} from './modelDetailsBooksPublications.utils';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const DrawerBody = styled.div`
    padding: 0 0.5rem 1rem;
`;

const WebsiteSection = styled.div`
    &:not(:first-child) {
        margin-top: 1.5rem;
    }

    .website-header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 1rem;
        margin-bottom: 0.75rem;
    }

    .website-name {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        font-size: ${(props) => props.theme.FontSizeLg};
        font-weight: 600;
        text-transform: capitalize;
    }

    .website-meta {
        color: ${(props) => props.theme.TextColorMuted};
        font-size: ${(props) => props.theme.FontSizeSm};
    }
`;

const DivisionSection = styled.div`
    padding-left: 1rem;

    &:not(:first-child) {
        margin-top: 1rem;
        padding-top: 1rem;
    }

    .division-header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 1rem;
        margin-bottom: 0.5rem;
    }

    .division-name {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        font-size: ${(props) => props.theme.FontSizeBase};
        font-weight: 600;
    }

    .division-meta {
        color: ${(props) => props.theme.TextColorMuted};
        font-size: ${(props) => props.theme.FontSizeSm};
    }
`;

const getBooksLabel = (count) => `${count} book${count === 1 ? '' : 's'}`;
const getDivisionsLabel = (count) => `${count} division${count === 1 ? '' : 's'}`;

const ModelDetailsBooksPublicationsContent = ({
    isLoading,
    onRemovePublication,
    publicationsSummary,
    readOnly,
    removingPublicationKey,
}) => {
    return (
        <DrawerBody>
            {isLoading ? (
                <Loading textBlack />
            ) : publicationsSummary.length > 0 ? (
                publicationsSummary.map((website) => (
                    <WebsiteSection key={website.key}>
                        <div className="website-header">
                            <div className="website-name">
                                <NwIcon icon={byPrefixAndName.fasl['globe']} />
                                <span>{website.websiteName}</span>
                            </div>
                            <div className="website-meta">
                                {getBooksLabel(website.booksCount)} in {getDivisionsLabel(website.divisionsCount)}
                            </div>
                        </div>
                        {website.divisions.map((division) => (
                            <DivisionSection key={division.key}>
                                <div className="division-header">
                                    <div className="division-name">
                                        <NwIcon icon={byPrefixAndName.fasl['list']} />
                                        <span>{division.divisionName || division.divisionCode}</span>
                                    </div>
                                    <div className="division-meta">{getBooksLabel(division.booksCount)}</div>
                                </div>
                                <List
                                    itemLayout="horizontal"
                                    dataSource={division.books}
                                    renderItem={(bookPublication) => (
                                        <PublishedBookListItem
                                            item={bookPublication}
                                            onRemove={!readOnly ? onRemovePublication : null}
                                            removeDisabled={Boolean(removingPublicationKey)}
                                            removeLoading={removingPublicationKey === bookPublication.key}
                                        />
                                    )}
                                />
                            </DivisionSection>
                        ))}
                    </WebsiteSection>
                ))
            ) : (
                <EmptyState message="No website publications" />
            )}
        </DrawerBody>
    );
};

const ModelDetailsBooksPublicationsDrawer = ({
    contactID,
    excludedPublicationKeys = [],
    modelName,
    onClose,
    onRemovePublication,
    playlists,
    readOnly = false,
    removingPublicationKey = null,
    renderAsDrawer = true,
    websiteData = [],
}) => {
    const hasProvidedPlaylists = playlists != null;
    const { data: fetchedPlaylists, isFetching: isFetchingPlaylists } = useContactPlaylists(
        contactID,
        hasProvidedPlaylists || !contactID
    );
    const [localHiddenPublicationKeys, setLocalHiddenPublicationKeys] = useState([]);

    const resolvedPlaylists = useMemo(() => {
        const providedPlaylists = coercePlaylistArray(playlists);
        if (providedPlaylists) {
            return [...providedPlaylists];
        }

        const remotePlaylists = coercePlaylistArray(fetchedPlaylists);
        if (remotePlaylists) {
            return [...remotePlaylists];
        }

        return hasProvidedPlaylists ? [] : null;
    }, [fetchedPlaylists, hasProvidedPlaylists, playlists]);

    const availablePublicationKeys = useMemo(
        () => getPlaylistPublicationKeys(resolvedPlaylists || []),
        [resolvedPlaylists]
    );

    useEffect(() => {
        const availableKeysSet = new Set(availablePublicationKeys);

        setLocalHiddenPublicationKeys((currentHiddenKeys) => {
            const nextHiddenKeys = currentHiddenKeys.filter((key) => availableKeysSet.has(key));

            return nextHiddenKeys.length === currentHiddenKeys.length ? currentHiddenKeys : nextHiddenKeys;
        });
    }, [availablePublicationKeys]);

    const hiddenPublicationKeySet = useMemo(
        () => new Set([...(excludedPublicationKeys || []), ...localHiddenPublicationKeys]),
        [excludedPublicationKeys, localHiddenPublicationKeys]
    );

    const publicationsSummary = useMemo(
        () => buildModelPublicationsSummary(resolvedPlaylists || [], websiteData, hiddenPublicationKeySet),
        [hiddenPublicationKeySet, resolvedPlaylists, websiteData]
    );

    const firstPublishedPlaylist =
        publicationsSummary[0]?.divisions?.[0]?.books?.[0]?.Playlist || resolvedPlaylists?.[0];
    const resolvedModelName =
        modelName || (firstPublishedPlaylist?.ContactID ? getContactName(firstPublishedPlaylist.ContactID) : 'Model');
    const websitesLabel = publicationsSummary.length === 1 ? 'website' : 'websites';
    const isLoading = !hasProvidedPlaylists && Boolean(contactID) && isFetchingPlaylists && !resolvedPlaylists;

    const handleRemovePublication = async (item) => {
        if (!onRemovePublication) {
            return false;
        }

        const didRemovePublication = await onRemovePublication(item);

        if (didRemovePublication) {
            setLocalHiddenPublicationKeys((currentHiddenKeys) =>
                currentHiddenKeys.includes(item.key) ? currentHiddenKeys : [...currentHiddenKeys, item.key]
            );
        }

        return didRemovePublication;
    };

    const content = (
        <ModelDetailsBooksPublicationsContent
            isLoading={isLoading}
            onRemovePublication={handleRemovePublication}
            publicationsSummary={publicationsSummary}
            readOnly={readOnly}
            removingPublicationKey={removingPublicationKey}
        />
    );

    if (!renderAsDrawer) {
        return content;
    }

    return (
        <NwDrawer title={`${resolvedModelName} on ${websitesLabel}`} width="48rem" onClose={onClose}>
            {content}
        </NwDrawer>
    );
};

export default ModelDetailsBooksPublicationsDrawer;
